import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Lock, ArrowRight, CheckCircle2 } from 'lucide-react';

// Web-only /reset-password route — the destination of the emailed
// password-reset link (…/reset-password?token=<token>). Renders
// regardless of auth state (a user resetting their password is logged
// out). On success the backend invalidates every session, so we bounce
// the user to the sign-in screen to log in with the new password.
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Accept `token` (current format) and the legacy `reset_token` param.
  const token = params.get('token') || params.get('reset_token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('This reset link is missing its token. Request a new one from the sign-in screen.');
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      const { data } = await authAPI.passwordResetConfirm(token, password);
      setDone(true);
      toast.success(data?.message || 'Password updated. Please sign in.');
      setTimeout(() => navigate('/', { replace: true }), 1800);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError((typeof d === 'string' ? d : d?.message) ||
        'This reset link is invalid, expired, or already used. Request a new one.');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-background" data-testid="reset-password-page">
      <div className="absolute inset-0 overflow-hidden">
        <div className="spotlight-overlay absolute inset-0" />
      </div>

      <div className="relative z-10 w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            Fantasy World Star
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Set a new password</p>
        </div>

        <div className="bg-card border border-border/60 rounded-2xl p-6 shadow-sm">
          {done ? (
            <div className="text-center space-y-3" data-testid="reset-password-done">
              <CheckCircle2 className="h-10 w-10 mx-auto text-[hsl(var(--chart-5))]" />
              <h2 className="text-lg font-semibold">Password updated</h2>
              <p className="text-sm text-muted-foreground">
                Taking you to the sign-in screen…
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Lock className="h-4 w-4" /> Choose a new password
              </h2>
              <Input
                type="password"
                placeholder="New password (min 8 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="reset-password-new"
              />
              <Input
                type="password"
                placeholder="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
                data-testid="reset-password-confirm"
              />
              {error && <p className="text-sm text-destructive" data-testid="reset-password-error">{error}</p>}
              <p className="text-[10px] text-muted-foreground">
                For your security, all devices will be signed out after you reset.
              </p>
              <Button
                type="submit"
                className="w-full"
                disabled={busy || !token}
                data-testid="reset-password-submit"
              >
                {busy ? 'Updating…' : 'Update password'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <button
                type="button"
                className="w-full text-xs text-muted-foreground underline"
                onClick={() => navigate('/', { replace: true })}
                data-testid="reset-password-back-link"
              >
                Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
